import {
  FundingTemplateFieldOverride,
  FundingTemplateFormField,
} from '../../api/fundingApi'

export type FieldOverrideState = Record<string, FundingTemplateFieldOverride>

const leafPath = (path: string) => {
  const parts = path.split('.')
  return parts[parts.length - 1] ?? path
}

const humanize = (label: string) =>
  label
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim()

const isDateLeaf = (leaf: string) => /Date$/i.test(leaf) || leaf === 'StartDate' || leaf === 'EndDate'

const looksLikeUrl = (value: string) => {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

export const resolveTemplateFieldValue = (
  field: FundingTemplateFormField,
  overrides: FieldOverrideState,
  globalDefaults?: Record<string, string | null> | null,
  globalsConfigured = false
): string => {
  const override = overrides[field.path]
  const mode = override?.mode ?? (globalsConfigured ? 'inherit' : 'override')
  if (mode === 'inherit' && globalsConfigured) {
    return String(globalDefaults?.[field.path] ?? '').trim()
  }
  return String(override?.value ?? '').trim()
}

export const validatePricingCalculatorUrl = (url: string): string | null => {
  const trimmed = url.trim()
  if (!trimmed) return null
  if (!looksLikeUrl(trimmed)) {
    return 'Enter a valid URL (https://…).'
  }
  return null
}

/**
 * Template values are defaults for runtime forms — schema `required` is not
 * enforced here. Only validate shape of values that are actually set.
 */
export const validateTemplateFields = ({
  fields,
  overrides,
  globalDefaults,
  globalsConfigured = false,
}: {
  fields: FundingTemplateFormField[]
  overrides: FieldOverrideState
  globalDefaults?: Record<string, string | null> | null
  globalsConfigured?: boolean
}): Record<string, string> => {
  const errors: Record<string, string> = {}

  for (const field of fields) {
    const value = resolveTemplateFieldValue(
      field,
      overrides,
      globalDefaults,
      globalsConfigured
    )
    const label = humanize(field.label)
    const leaf = leafPath(field.path)

    if (!value) continue

    if (field.max_length && value.length > field.max_length) {
      errors[field.path] = `${label} must be at most ${field.max_length} characters.`
      continue
    }

    if (leaf === 'AwsAccountId') {
      if (!/^\d+$/.test(value)) {
        errors[field.path] = 'AWS account ID must contain only numbers.'
        continue
      }
      if (!/^\d{12}$/.test(value)) {
        errors[field.path] = 'AWS account ID must be exactly 12 digits.'
        continue
      }
    }

    if (field.type === 'number' || field.type === 'integer') {
      if (!/^-?\d+(\.\d+)?$/.test(value)) {
        errors[field.path] = `${label} must be a number.`
        continue
      }
    }

    if (field.type === 'boolean') {
      const normalized = value.toLowerCase()
      if (!['true', 'false', 'yes', 'no'].includes(normalized)) {
        errors[field.path] = `${label} must be Yes or No.`
        continue
      }
    }

    if (isDateLeaf(leaf) || field.type === 'date' || field.type === 'datetime') {
      // Accept YYYY-MM-DD from date inputs; reject free-text.
      if (!/^\d{4}-\d{2}-\d{2}/.test(value) || Number.isNaN(Date.parse(value))) {
        errors[field.path] = `${label} must be a valid date.`
        continue
      }
    }

    if (field.enum && field.enum.length > 0) {
      const parts =
        leaf === 'ExecutedState' || field.type === 'array'
          ? value.split(',').map(s => s.trim()).filter(Boolean)
          : [value]
      const invalid = parts.filter(part => !field.enum!.includes(part))
      if (invalid.length > 0) {
        errors[field.path] = `${label} has an invalid selection.`
      }
    }
  }

  return errors
}
